import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'

import { RootState } from '@redux/store'

import { CriteriaType } from '@constants'
import { GETSearchType } from '@types'

type SearchAssistantStateValue = {
  selectedFavouriteCriteria: GETSearchType | null
  defaultSearchFilter?: GETSearchType | null
  // Все критерии полученные с бэкенда, но для локального использования
  // Не является источником правды, для получения актуальных критериев использовать criterias получение из query хука
  criterias: CriteriaType[]
}

type SearchAssistantState = {
  [id: string]: SearchAssistantStateValue
}

const initialState: SearchAssistantState = {}

type SearchAssistantPayloadAction<ModelName extends string, Model> = PayloadAction<
  { searchAssistantKey: string } & Record<ModelName, Model>
>

export const searchAssistantSlice = createSlice({
  name: 'searchAssistant',
  initialState,
  reducers: {
    saveDefaultSearchFilter(
      state,
      action: SearchAssistantPayloadAction<'defaultSearchFilter', GETSearchType | null>
    ) {
      const { searchAssistantKey, defaultSearchFilter } = action.payload

      if (!state[searchAssistantKey]) {
        state[searchAssistantKey] = {
          defaultSearchFilter: null,
          selectedFavouriteCriteria: null,
          criterias: [],
        }
      }

      state[searchAssistantKey].defaultSearchFilter = defaultSearchFilter
    },
    saveSelectedFavouriteCriteria: (
      state,
      action: SearchAssistantPayloadAction<'selectedFavouriteCriteria', GETSearchType | null>
    ) => {
      const { searchAssistantKey, selectedFavouriteCriteria } = action.payload

      if (!state[searchAssistantKey]) {
        state[searchAssistantKey] = {
          defaultSearchFilter: null,
          selectedFavouriteCriteria: null,
          criterias: [],
        }
      }

      state[searchAssistantKey].selectedFavouriteCriteria = selectedFavouriteCriteria
    },
    saveCriterias: (state, action: SearchAssistantPayloadAction<'criterias', CriteriaType[]>) => {
      const { searchAssistantKey, criterias } = action.payload

      if (!state[searchAssistantKey]) {
        state[searchAssistantKey] = {
          selectedFavouriteCriteria: null,
          criterias: [],
        }
      }

      state[searchAssistantKey].criterias = criterias
    },
    disposeSearchAssistant: (
      state,
      action: PayloadAction<{ searchAssistantKey: string | undefined }>
    ) => {
      const { searchAssistantKey } = action.payload

      if (searchAssistantKey) {
        delete state[searchAssistantKey]
      }
    },
  },
})

export const getDefaultSearchFilter = createSelector(
  (state: RootState) => state.searchAssistantSlice,
  (_: RootState, searchAssistantKey: string | undefined) => searchAssistantKey,
  (searchAssistantSlice, searchAssistantKey) => {
    if (!searchAssistantKey) {
      return null
    }

    return searchAssistantSlice[searchAssistantKey]?.defaultSearchFilter
  }
)

export const getSelectedFavouriteCriteria = createSelector(
  (state: RootState) => state.searchAssistantSlice,
  (_: RootState, searchAssistantKey: string | undefined) => searchAssistantKey,
  (searchAssistantSlice, searchAssistantKey) => {
    if (!searchAssistantKey) {
      return null
    }

    return searchAssistantSlice[searchAssistantKey]?.selectedFavouriteCriteria
  }
)

export const getCriterias = createSelector(
  (state: RootState) => state.searchAssistantSlice,
  (_: RootState, searchAssistantKey: string | undefined) => searchAssistantKey,
  (searchAssistantSlice, searchAssistantKey) => {
    if (!searchAssistantKey) {
      return []
    }

    return searchAssistantSlice?.[searchAssistantKey]?.criterias || []
  }
)

export const {
  saveDefaultSearchFilter,
  saveSelectedFavouriteCriteria,
  saveCriterias,
  disposeSearchAssistant,
} = searchAssistantSlice.actions
